import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { FieldType, FilledBy, FormField, SelectOption } from "@/models";
import { FileText, Calendar, ListFilter, Info, Upload, PenLine, Pen } from "lucide-react";
import WysiwygEditor from "../WysiwygEditor";
import SelectOptionsEditor from "../SelectOptionsEditor";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";

interface SimpleAddFieldDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAddField?: (data: {
    label: string;
    name?: string;
    fieldType: FieldType;
    isRequired: boolean;
    filledBy: FilledBy;
    content?: string;
    options?: SelectOption[];
    dataType?: string;
    mainApplicantOnly?: boolean;
  }) => Promise<void>;
  field?: FormField | null;
  onUpdate?: (
    label: string, 
    fieldType: string, 
    filledBy: string, 
    isRequired: boolean, 
    content?: string, 
    options?: SelectOption[],
    name?: string,
    dataType?: string
  ) => Promise<void>;
  isSubmitting: boolean;
}

const fieldTypeOptions = [
  { value: 'text', label: 'Text Field', icon: <FileText className="h-4 w-4" /> },
  { value: 'date', label: 'Date Field', icon: <Calendar className="h-4 w-4" /> },
  { value: 'select', label: 'Select Field', icon: <ListFilter className="h-4 w-4" /> },
  { value: 'information', label: 'Information Block', icon: <Info className="h-4 w-4" /> },
  { value: 'file', label: 'File Upload', icon: <Upload className="h-4 w-4" /> },
  { value: 'signature', label: 'Signature', icon: <PenLine className="h-4 w-4" /> },
  { value: 'initials', label: 'Initials', icon: <Pen className="h-4 w-4" /> }
];

const dataTypeOptions = [
  { value: 'generic', label: 'Generic' },
  { value: 'email', label: 'Email' },
  { value: 'address', label: 'Address' },
  { value: 'state', label: 'State' },
  { value: 'phone', label: 'Phone' },
  { value: 'country', label: 'Country' },
  { value: 'social_security', label: 'Social Security Number' },
  { value: 'date_of_birth', label: 'Date of Birth' },
];

const SimpleAddFieldDialog = ({ 
  isOpen, 
  onClose,
  onAddField,
  field = null,
  onUpdate,
  isSubmitting
}: SimpleAddFieldDialogProps) => {
  const isEditMode = !!field;
  console.log("SimpleAddFieldDialog rendered", isEditMode ? "in edit mode" : "in add mode");
  
  const { toast } = useToast();
  const [label, setLabel] = useState("");
  const [name, setName] = useState("");
  const [fieldType, setFieldType] = useState<FieldType>("text");
  const [isRequired, setIsRequired] = useState(false);
  const [filledBy, setFilledBy] = useState<FilledBy>("requestor");
  const [content, setContent] = useState("");
  const [options, setOptions] = useState<SelectOption[]>([
    { label: "Option 1", value: "option_1" }
  ]);
  const [dataType, setDataType] = useState<string>("generic");
  // Multi-applicant signatures (D1): only shown for signature/initials.
  const [mainApplicantOnly, setMainApplicantOnly] = useState(false);
  const isSignatureField = fieldType === "signature" || fieldType === "initials";

  const generateNameFromLabel = (inputLabel: string): string => {
    return inputLabel
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')     
      .trim()                        
      .split(/\s+/)                  
      .slice(0, 6)                   
      .join('_');
  };

  const parseOptionsFromContent = (contentStr: string | undefined): SelectOption[] => {
    if (!contentStr) return [{ label: "Option 1", value: "option_1" }];
    
    try {
      const parsedOptions = JSON.parse(contentStr);
      console.log("Parsed options from content:", parsedOptions);
      
      if (Array.isArray(parsedOptions)) {
        return parsedOptions.map(opt => {
          if (typeof opt === 'object' && opt !== null) {
            return {
              label: opt.label || opt.text || "Option",
              value: opt.value || "option"
            };
          }
          return { label: String(opt), value: String(opt) };
        });
      }
    } catch (error) {
      console.error("Error parsing options from content:", error);
    }
    
    return [{ label: "Option 1", value: "option_1" }];
  };

  useEffect(() => {
    if (isOpen) {
      if (field) {
        console.log("Loading field for edit:", field);
        setLabel(field.label || "");
        setName(field.name || "");
        setFieldType(field.field_type);
        setIsRequired(field.is_required);
        setFilledBy(field.filled_by);
        
        const fieldDataType = field.data_type || "generic";
        console.log("Setting data type to:", fieldDataType);
        setDataType(fieldDataType);
        
        if (field.field_type === "information") {
          setContent(field.content || "");
        } else if (field.field_type === "select") {
          if (field.options && field.options.length > 0) {
            console.log("Using field.options:", field.options);
            setOptions(field.options);
          } else if (field.content) {
            console.log("Parsing options from content:", field.content);
            const parsedOptions = parseOptionsFromContent(field.content);
            setOptions(parsedOptions);
          }
        }
      } else {
        setLabel("");
        setName("");
        setFieldType("text");
        setIsRequired(false);
        setFilledBy("requestor");
        setContent("");
        setOptions([{ label: "Option 1", value: "option_1" }]);
        setDataType("generic");
      }
    }
  }, [isOpen, field]);

  useEffect(() => {
    if (!isEditMode && label) {
      setName(generateNameFromLabel(label));
    }
  }, [label, isEditMode]);
  
  const handleSubmit = async () => {
    console.log("handleSubmit called with form values:", { label, name, fieldType, isRequired, filledBy, dataType });
    
    if (!label.trim() && fieldType !== 'information') {
      toast({
        variant: "destructive",
        title: "Label is required",
        description: "Please enter a label for the field"
      });
      return;
    }
    
    if (fieldType === 'information' && !content.trim()) {
      toast({
        variant: "destructive",
        title: "Content is required",
        description: "Please enter content for the information field"
      });
      return;
    }
    
    if (fieldType === 'select' && (!options.length || !options[0].label.trim())) {
      toast({
        variant: "destructive",
        title: "Options are required",
        description: "Please add at least one option for the select field"
      });
      return;
    }
    
    try {
      if (isEditMode && onUpdate) {
        console.log("Updating field...");
        
        let fieldContent;
        if (fieldType === 'information') {
          fieldContent = content;
        } else if (fieldType === 'select') {
          fieldContent = JSON.stringify(options);
        }
        
        await onUpdate(
          label.trim(), 
          fieldType,
          filledBy,
          isRequired,
          fieldContent,
          fieldType === 'select' ? options : undefined,
          name.trim() || generateNameFromLabel(label),
          dataType
        );
      } else if (!isEditMode && onAddField) {
        const fieldData = {
          label: fieldType === 'information' ? 'Information' : label.trim(),
          name: name.trim() || generateNameFromLabel(label),
          fieldType,
          isRequired,
          filledBy,
          content: fieldType === 'information' ? content : 
                  fieldType === 'select' ? JSON.stringify(options) : undefined,
          options: fieldType === 'select' ? options : undefined,
          dataType,
          mainApplicantOnly: isSignatureField ? mainApplicantOnly : false
        };
        
        console.log("Adding new field with data:", fieldData);
        await onAddField(fieldData);
      }
    } catch (error) {
      console.error("Error processing field:", error);
      toast({
        variant: "destructive",
        title: `Failed to ${isEditMode ? 'update' : 'add'} field`,
        description: error instanceof Error ? error.message : "An unknown error occurred"
      });
    }
  };
  
  const getFieldIcon = (type: FieldType) => {
    switch (type) {
      case 'text':
        return <FileText className="h-4 w-4 text-green-500" />;
      case 'date':
        return <Calendar className="h-4 w-4 text-amber-500" />;
      case 'select':
        return <ListFilter className="h-4 w-4 text-cyan-500" />;
      case 'information':
        return <Info className="h-4 w-4 text-blue-500" />;
      case 'file':
        return <Upload className="h-4 w-4 text-red-500" />;
      case 'signature':
        return <PenLine className="h-4 w-4 text-violet-500" />;
      case 'initials':
        return <Pen className="h-4 w-4 text-indigo-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };
  
  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        if (!isSubmitting) {
          onClose();
        }
      }}
    >
      <DialogContent 
        className="sm:max-w-[600px] max-h-[80vh] flex flex-col overflow-scroll" 
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onPointerDownOutside={(e) => {
          if (e.target instanceof Element) {
            const target = e.target as Element;
            if (target.closest('[data-radix-popper-content-wrapper]')) {
              e.preventDefault();
            }
          }
          if (isSubmitting) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Field' : 'Add New Field'}</DialogTitle>
          <DialogDescription>
            {isEditMode ? 'Modify this field\'s properties' : 'Create a new field in this section'}
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="flex-grow px-4">
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="field-type">Field Type</Label>
              <Select
                value={fieldType}
                onValueChange={(value) => {
                  console.log("Field type changed to", value);
                  setFieldType(value as FieldType);
                }}
                disabled={isSubmitting || isEditMode}
              >
                <SelectTrigger id="field-type">
                  <SelectValue placeholder="Select field type">
                    {fieldType && (
                      <div className="flex items-center space-x-2">
                        {getFieldIcon(fieldType)}
                        <span>{fieldType.charAt(0).toUpperCase() + fieldType.slice(1)}</span>
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {fieldTypeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center space-x-2">
                        {option.icon}
                        <span>{option.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
        
            {fieldType !== 'information' && (
              <div className="space-y-2">
                <Label htmlFor="field-label">Field Label</Label>
                <Input
                  id="field-label"
                  placeholder="Enter field label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            )}
          
            {fieldType !== 'information' && (
              <div className="space-y-2">
                <Label htmlFor="field-name">Field Name</Label>
                <Input
                  id="field-name"
                  placeholder="Enter field name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  System identifier for this field. Auto-generated from label if left blank.
                </p>
              </div>
            )}
        
            {fieldType === 'information' && (
              <div className="space-y-2">
                <Label htmlFor="field-content">Information Content</Label>
                <WysiwygEditor
                  value={content}
                  onChange={(newContent) => setContent(newContent)}
                  disabled={isSubmitting}
                />
              </div>
            )}
        
            {fieldType === 'select' && (
              <div className="space-y-2">
                <Label htmlFor="field-options">Options</Label>
                <SelectOptionsEditor
                  options={options}
                  onChange={(newOptions) => setOptions(newOptions)}
                  disabled={isSubmitting}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Data Type</Label>
              <Select
                value={dataType}
                onValueChange={(value) => {
                  console.log("Data type changed to:", value);
                  setDataType(value);
                }}
                disabled={isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select data type" />
                </SelectTrigger>
                <SelectContent>
                  {dataTypeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Specifies the type of data this field will collect.
              </p>
            </div>
        
            {fieldType !== 'information' && (
              <div>
                <div className="space-y-2">
                  <Label htmlFor="filled-by">Filled By</Label>
                  <Select
                    value={filledBy}
                    onValueChange={(value) => setFilledBy(value as FilledBy)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger id="filled-by">
                      <SelectValue placeholder="Select who fills this field" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="requestor">Requestor</SelectItem>
                      <SelectItem value="operations">Operations</SelectItem>
                      <SelectItem value="system">System</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {isSignatureField && (
                  <div className="rounded-md border border-border bg-muted/40 p-3 mt-2">
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="add-main-applicant-only"
                        checked={mainApplicantOnly}
                        onCheckedChange={(checked) =>
                          setMainApplicantOnly(checked === true)
                        }
                        disabled={isSubmitting}
                      />
                      <div className="space-y-1">
                        <Label
                          htmlFor="add-main-applicant-only"
                          className="text-sm font-medium leading-none cursor-pointer"
                        >
                          Only the main applicant
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {mainApplicantOnly
                            ? "Only the main applicant signs this field."
                            : "Every applicant on the order signs this field."}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox
                    id="is-required"
                    checked={isRequired}
                    onCheckedChange={(checked) => setIsRequired(checked === true)}
                    disabled={isSubmitting}
                  />
                  <Label
                    htmlFor="is-required"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    onClick={() => {
                      if (!isSubmitting) {
                        setIsRequired(!isRequired);
                      }
                    }}
                  >
                    Required Field
                  </Label>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="pt-4 border-t mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              console.log("Cancel button clicked");
              onClose();
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="button"
            disabled={isSubmitting}
            onClick={() => {
              console.log(isEditMode ? "Update Field button clicked" : "Add Field button clicked");
              handleSubmit();
            }}
          >
            {isEditMode ? 'Update Field' : 'Add Field'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SimpleAddFieldDialog;
